import React, { useCallback, useRef, useState } from "react";
import cn from "classnames";
import DOMPurify from "dompurify";
import { format, formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { actions as mainActions } from "../../actions/main";
import { ReactComponent as MainLogo } from "../../media/icons/mainLogo.svg";
import { getFormattedDateTimeZone } from "../../helpers";
import { logoutOfKeycloak } from "../../helpers/keycloakLogout";
import { parseDate } from "../../helpers/parseDate";
import MainSearchV2 from "../mainSearch/v2";
import { useDismiss } from "./hooks";
import { ShellIcon } from "./icons";
import { useWalletBalance } from "./useWalletBalance";
import { HERITAGE_CREDIT_V2 } from "../../constants/featureFlags";
import styles from "./appShell.module.css";

const HELP_URL = "https://help.heritageweb.com/en/";
const POPUP_NOTIFICATIONS = 4;

/** "12 minutes ago" for a notification's `added`, the way the old popup reads it; "" when the date does not parse. */
const timeAgo = added => {
  try {
    const stamp = format(getFormattedDateTimeZone(added), "yyyy-MM-dd' 'HH:mm:ss");

    return formatDistanceToNow(new Date(parseDate(stamp)), { addSuffix: true });
  } catch {
    return "";
  }
};

/** A notification's message with its markup removed and its entities decoded ("Tom &amp; Jerry" reads "Tom & Jerry"), for the popover row. */
const plainText = message => DOMPurify.sanitize(String(message || ""), { ALLOWED_TAGS: [], RETURN_DOM_FRAGMENT: true }).textContent;

const CreditPill = ({ balance }) => {
  const { t } = useTranslation();
  const amount = Number(balance).toLocaleString("en-US");

  return (
    <Link to="/wallet" className={styles.creditPill} aria-label={t("dashboard_shell_credits_aria", { amount })}>
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <circle cx="10" cy="10" r="9" fill="#f6c343" />
        <circle cx="10" cy="10" r="6.5" fill="none" stroke="#d9a21b" strokeWidth="1.5" />
      </svg>
      {amount}
      <span className={styles.creditLabel}>{t("dashboard_shell_credits")}</span>
    </Link>
  );
};

const NotificationsPopover = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const profile = useSelector(state => state.account.profile);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const unread = Math.max(0, Number(profile.unread_notifications) || 0);
  const rows = (profile.notifications || []).slice(0, POPUP_NOTIFICATIONS);

  useDismiss(open, close, wrapRef, buttonRef);

  return (
    <div ref={wrapRef} className={styles.popWrap}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.iconButton}
        aria-label={unread > 0 ? t("dashboard_shell_bell_unread", { count: unread }) : t("dashboard_shell_bell")}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(!open)}
      >
        <ShellIcon name="notification" size={22} />
        {unread > 0 && <span className={styles.bellBadge}>{unread}</span>}
      </button>
      {open && (
        <div role="dialog" aria-label={t("dashboard_shell_bell")} className={cn(styles.popover, styles.bellPopover)}>
          <div className={styles.popHeader}>
            <span className={styles.popTitle}>{t("dashboard_shell_bell")}</span>
            {unread > 0 && (
              <button type="button" className={styles.markAll} onClick={() => dispatch(mainActions.setViewAllNotification())}>
                {t("dashboard_shell_mark_all")}
              </button>
            )}
          </div>
          {rows.length === 0 ? (
            <p className={styles.notifEmpty}>{t("dashboard_shell_no_notifications")}</p>
          ) : (
            <ul className={styles.notifList}>
              {rows.map(row => (
                <li key={row.notification_id} className={styles.notifRow}>
                  <span className={styles.notifIcon}>
                    <ShellIcon name="notification" size={18} />
                  </span>
                  <span className={styles.notifBody}>
                    <span className={styles.notifText}>{plainText(row.message)}</span>
                    <span className={styles.notifTime}>{timeAgo(row.added)}</span>
                  </span>
                  {String(row.is_viewed) !== "1" && (
                    <span className={styles.unreadDot} role="img" aria-label={t("dashboard_shell_unread")} />
                  )}
                </li>
              ))}
            </ul>
          )}
          <Link to="/notifications" className={styles.seeAll} onClick={close}>
            {t("dashboard_shell_see_all")}
          </Link>
        </div>
      )}
    </div>
  );
};

const Avatar = ({ profile, large }) => (
  <span className={cn(styles.avatar, large && styles.avatarLarge)}>
    {profile.cover_image_url ? <img className={styles.avatarImage} src={profile.cover_image_url} alt="" /> : profile.initials}
  </span>
);

const AccountMenu = ({ keycloak, accountItems, activeId }) => {
  const { t } = useTranslation();
  const profile = useSelector(state => state.account.profile);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const fullName = [profile.name_f, profile.name_l].filter(Boolean).join(" ");

  useDismiss(open, close, wrapRef, buttonRef);

  return (
    <div ref={wrapRef} className={styles.popWrap}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.accountButton}
        aria-label={t("dashboard_shell_account_menu")}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Avatar profile={profile} />
        <span className={styles.accountName}>{profile.name_f}</span>
        <span className={styles.accountChevron}>
          <ShellIcon name="chevron" size={14} />
        </span>
      </button>
      {open && (
        <div role="group" aria-label={t("dashboard_shell_item_account")} className={cn(styles.popover, styles.accountPopover)}>
          <div className={styles.identity}>
            <Avatar profile={profile} large />
            <span className={styles.identityText}>
              <span className={styles.identityName}>{fullName}</span>
              <span className={styles.identityEmail}>{profile.email}</span>
            </span>
          </div>
          {profile.profiles_listing_id && (
            <a
              href={`/public-profile/${profile.profiles_listing_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.menuItem}
              onClick={close}
            >
              <ShellIcon name="eye" />
              {t("dashboard_shell_view_public")}
            </a>
          )}
          <span className={styles.menuRule} />
          <span className={styles.menuLabel}>{t("dashboard_shell_group_account")}</span>
          {accountItems.map(item => (
            <Link
              key={item.id}
              to={item.to}
              onClick={close}
              className={cn(styles.menuItem, item.id === activeId && styles.itemActive)}
              aria-current={item.id === activeId ? "page" : undefined}
            >
              <ShellIcon name={item.icon} />
              {t(item.label)}
            </Link>
          ))}
          <span className={styles.menuRule} />
          <a href={HELP_URL} target="_blank" rel="noopener noreferrer" className={styles.menuItem} onClick={close}>
            <ShellIcon name="help" />
            {t("dashboard_shell_help")}
          </a>
          <button
            type="button"
            className={cn(styles.menuItem, styles.logout)}
            onClick={() => logoutOfKeycloak(keycloak, { redirectUri: `${window.location.origin}/login` })}
          >
            <ShellIcon name="logout" />
            {t("dashboard_shell_logout")}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * The 72px header (56px under 768px): the menu button and logo when no sidebar is on screen, the directory search, the Heritage
 * Credit pill (only with the feature on and a balance), the notifications bell and the account menu.
 *
 * @param {Object} props
 * @param {Object} props.keycloak the Keycloak instance Log out ends the session on
 * @param {boolean} props.showMenu whether the menu button and logo show (no sidebar on screen)
 * @param {Function} props.onOpenMenu opens the sidebar (wide) or the drawer (narrow)
 * @param {boolean} props.drawerOpen the menu button's aria-expanded
 * @param {Array} props.accountItems the nav's Account group items, listed in the account menu
 * @param {string} props.activeId the active page id
 */
export const Header = ({ keycloak, showMenu, onOpenMenu, drawerOpen, accountItems, activeId }) => {
  const { t } = useTranslation();
  const balance = useWalletBalance(HERITAGE_CREDIT_V2);

  return (
    <header className={styles.header}>
      <div className={styles.headerInner}>
        {showMenu && (
          <>
            <button
              type="button"
              className={styles.menuButton}
              onClick={onOpenMenu}
              aria-label={t("dashboard_shell_open_menu")}
              aria-expanded={drawerOpen}
              aria-controls="app-shell-drawer"
            >
              <ShellIcon name="menu" size={24} />
            </button>
            <Link to="/" className={styles.headerLogo} aria-label={t("dashboard_shell_logo_label")}>
              <MainLogo className={styles.logoImage} />
            </Link>
          </>
        )}
        <div className={styles.searchSlot}>
          <MainSearchV2 />
        </div>
        <div className={styles.actions}>
          {balance !== null && <CreditPill balance={balance} />}
          <NotificationsPopover />
          <AccountMenu keycloak={keycloak} accountItems={accountItems} activeId={activeId} />
        </div>
      </div>
    </header>
  );
};
